import { z } from "zod";
import { capture } from "@/lib/sentry/capture";
import { buildingPlanTierSchema } from "./types";

const draftSchema = z.object({
  userId: z.string(),
  orgId: z.string(),
  createdAt: z.number(),
  tier: buildingPlanTierSchema,
  values: z.object({ name: z.string(), address: z.string(), registry_code: z.string() }),
});
export type BuildingSetupDraft = z.infer<typeof draftSchema>;
const key = "franklin.building-billing-setup";

function reportStorageFailure(error: unknown) {
  capture(error instanceof Error ? error : new Error("Building setup draft storage failed"), {
    tags: { feature: "billing", action: "building_setup_draft" },
    fingerprint: ["billing", "building_setup_draft", "storage"],
    context: { billing: { operation: "draft_storage" } },
  });
}

function matchesCurrentSetup(draft: BuildingSetupDraft, userId: string, orgId: string) {
  const age = Date.now() - draft.createdAt;
  return draft.userId === userId && draft.orgId === orgId && age >= 0 && age <= 86_400_000;
}

export function readBuildingSetupDraft(userId: string | null | undefined, orgId: string) {
  if (!userId || !orgId) return null;
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const result = draftSchema.safeParse(JSON.parse(raw));
    if (!result.success) return null;
    const draft = result.data;
    return matchesCurrentSetup(draft, userId, orgId) ? draft : null;
  } catch (error) {
    reportStorageFailure(error);
    return null;
  }
}

export function saveBuildingSetupDraft(draft: BuildingSetupDraft): boolean {
  try {
    sessionStorage.setItem(key, JSON.stringify(draftSchema.parse(draft)));
    return true;
  } catch (error) {
    reportStorageFailure(error);
    return false;
  }
}

export function clearBuildingSetupDraft(userId: string | null | undefined, orgId: string) {
  if (!readBuildingSetupDraft(userId, orgId)) return;
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    reportStorageFailure(error);
  }
}
